import React, { useEffect, useRef, useState, useMemo } from "react";
import { Form } from "react-router-dom";
import { renderField as dynamicRenderField } from "../../utils/forms/renderers/DynamicFieldRenderer";
import {
  deriveMetadataFromSections,
  getFieldsForSection,
  humanize,
  expandSectionFieldNames,
} from "../../utils/forms/schema/schemaResolver";
import FormSectionWrapper from "../ui/account/FormSectionWrapper";

/**
 * VerticalFormWizard - A multi-step form wizard with validation
 *
 * @param {Array} schemaSections - Array of form sections with field definitions
 * @param {Function} onSubmit - Callback called on last sub-step with full form data
 * @param {Function} onPrevFromFirstSubStep - Callback when going back from first step
 * @param {Object} initialFormState - Initial form state values
 * @param {Object} formMethods - react-hook-form methods (useForm hook result)
 * @param {Function} onValidationChange - Callback for validation state changes (isValid: boolean) => void
 * @param {string} nextButtonText - Custom text for the next/submit button (defaults to "Submit")
 * @param {Function} onBeforeNext - Callback before advancing to next step (step: number, event: Event) => Promise<boolean>
 *
 * @example
 * ```js
 * const [isCurrentStepValid, setIsCurrentStepValid] = useState(false);
 *
 * const handleBeforeNext = async (step, event) => {
 *   // Custom validation logic for specific steps
 *   if (step === 1) {
 *     // Perform address validation
 *     const isValid = await validateAddress();
 *     return isValid; // Return false to prevent navigation
 *   }
 *   return true; // Continue normally
 * };
 *
 * return (
 *   <VerticalFormWizard
 *     schemaSections={schemaSections}
 *     initialFormState={initialFormState}
 *     onSubmit={handleSubmit}
 *     onPrevFromFirstSubStep={handlePrev}
 *     formMethods={formMethods}
 *     onValidationChange={setIsCurrentStepValid}
 *     nextButtonText="Create Account"
 *     onBeforeNext={handleBeforeNext}
 *   />
 * );
 * ```
 */
function VerticalFormWizard({
  schemaSections = [],
  onSubmit = null, // (fullFormState) => void, only on last sub-step
  onSubStepComplete = null, // (fullFormState, subStepIndex) => void, called after each sub-step completion
  onPrevFromFirstSubStep = null,
  initialFormState = null,
  formMethods = null, // NEW: react-hook-form methods
  onValidationChange = null, // NEW: callback for validation state changes
  nextButtonText = "Submit", // NEW: custom text for the next button (defaults to "Submit")
  onBeforeNext = null, // NEW: callback before advancing to next step
}) {
  const resolvedSchema = useMemo(
    () => (Array.isArray(schemaSections) ? schemaSections : []),
    [schemaSections]
  );
  const metadata = useMemo(
    () => deriveMetadataFromSections(resolvedSchema),
    [resolvedSchema]
  );
  const totalSubSteps = metadata.subSteps;
  const dynamicStepTitle = metadata.name;

  // Current sub-step index (internal-only)
  const [internalSubStep, setInternalSubStep] = useState(0);
  const effectiveSubStep = internalSubStep;

  // Internal form state (fallback for non-react-hook-form mode)
  const [formState, setFormState] = useState(initialFormState || {});

  // Direction/animation (internal-only)
  const [internalDirection, setInternalDirection] = useState("next");
  const [internalAnimating, setInternalAnimating] = useState(false);
  const effectiveDirection = internalDirection;
  const effectiveAnimating = internalAnimating;
  const [errorSummary, setErrorSummary] = useState("");
  const [isCurrentStepValid, setIsCurrentStepValid] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const hasUserInteractedRef = useRef(false);
  const [hasAttemptedNext, setHasAttemptedNext] = useState(false); // State to trigger re-render when "Next" is clicked
  const isNavigatingRef = useRef(false); // Prevent multiple simultaneous navigations
  const [addressValidationError, setAddressValidationError] = useState(null); // Track address validation errors for alert
  // Force re-render on RHF value changes to reflect UI immediately (e.g., toggles)
  const [, setRenderTick] = useState(0);

  // Unique wizard instance id (stable per mount)
  const wizardIdRef = useRef(null);
  if (!wizardIdRef.current) {
    wizardIdRef.current = `vw-${Math.random().toString(36).slice(2)}`;
  }
  const wizardInstanceId = wizardIdRef.current;

  const progressPercentage =
    totalSubSteps > 1 ? ((effectiveSubStep + 1) / totalSubSteps) * 100 : 100;

  // use shared helper from schemaResolver

  // Track previous schema to detect actual changes
  const prevSchemaRef = useRef(resolvedSchema);
  const prevTotalStepsRef = useRef(totalSubSteps);
  
  // Reset sub-step when schema or service (or steps count) changes
  // Only reset if the schema actually changed, not on every render
  useEffect(() => {
    const schemaChanged = prevSchemaRef.current !== resolvedSchema;
    const stepsChanged = prevTotalStepsRef.current !== totalSubSteps;
    
    // Only reset if schema or total steps actually changed
    if (schemaChanged || stepsChanged) {
      setInternalSubStep(0);
      setInternalDirection("next");
      setFormState(initialFormState || {});
      // Reset validation state when schema changes
      setIsCurrentStepValid(false);
      setIsValidating(false);
      onValidationChange?.(false);

      // Update refs
      prevSchemaRef.current = resolvedSchema;
      prevTotalStepsRef.current = totalSubSteps;

      // Trigger initial validation after reset to check current form state
      // Note: This will be handled by the main validation useEffect below
      // which validates the current section based on effectiveSubStep
    }
  }, [
    resolvedSchema,
    totalSubSteps,
    initialFormState,
    onValidationChange,
    formMethods,
    metadata.subStepSections,
  ]);

  // Determine current section key based on metadata and sub-step
  // Ensure step index is within valid bounds
  const clampedStep = Math.max(0, Math.min(effectiveSubStep, totalSubSteps - 1));
  const currentSectionKey = Array.isArray(metadata.subStepSections) && metadata.subStepSections.length > 0
    ? metadata.subStepSections[clampedStep] ?? null
    : null;
  

  // Validation state management
  const lastValidationHashRef = useRef("");
  const hasValidatedOnMountRef = useRef(false);
  const formInitializedRef = useRef(false);
  const validationRetryCountRef = useRef(0);
  const validateCurrentStepRef = useRef(null);
  
  // Track when form is initialized with data
  useEffect(() => {
    if (formMethods && initialFormState) {
      // Check if form has been reset with initial data
      const formValues = formMethods.getValues();
      const hasData = Object.keys(formValues).length > 0 && 
        Object.values(formValues).some(v => v !== undefined && v !== null && v !== "");
      
      if (hasData) {
        formInitializedRef.current = true;
        validationRetryCountRef.current = 0;
      }
    }
  }, [formMethods, initialFormState]);
  
  // Reset form initialized flag when section changes
  useEffect(() => {
    formInitializedRef.current = false;
    validationRetryCountRef.current = 0;
  }, [currentSectionKey]);
  
  useEffect(() => {
    if (!formMethods || !currentSectionKey) {
      setIsCurrentStepValid(false);
      setIsValidating(false);
      onValidationChange?.(false);
      return;
    }

    let timeoutId = null;

    const validateCurrentStep = async (showErrors = false) => {
      if (isValidating) {
        return; // Prevent concurrent validations
      }
      
      // Wait a bit for form to be initialized if it hasn't been yet
      if (!formInitializedRef.current && initialFormState) {
        const formValues = formMethods.getValues();
        const hasData = Object.keys(formValues).length > 0 && 
          Object.values(formValues).some(v => v !== undefined && v !== null && v !== "");
        if (!hasData && validationRetryCountRef.current < 10) {
          validationRetryCountRef.current += 1;
          setTimeout(() => {
            validateCurrentStep(showErrors);
          }, 100);
          return;
        }
        if (hasData) {
          formInitializedRef.current = true;
          validationRetryCountRef.current = 0;
        } else {
          formInitializedRef.current = true; // Proceed anyway to avoid infinite wait
        }
      }
      
      setIsValidating(true);
      
      // If showErrors is false (called from watch), don't mark fields as touched
      // Only validate silently for button state
      if (showErrors) {
        // Mark all fields in current section as touched so errors will show
        const currentFields = getFieldsForSection({
          schema: resolvedSchema,
          sectionKey: currentSectionKey,
        });
        const sectionFieldNames = expandSectionFieldNames(currentFields || []);
        sectionFieldNames.forEach((fieldName) => {
          const currentVal = formMethods.getValues(fieldName);
          formMethods.setValue(fieldName, currentVal, {
            shouldTouch: true,
            shouldValidate: false, // We'll trigger validation separately
            shouldDirty: false,
          });
        });
      }

      const currentFields = getFieldsForSection({
        schema: resolvedSchema,
        sectionKey: currentSectionKey,
      });

      if (!currentFields || currentFields.length === 0) {
        setIsCurrentStepValid(true);
        setIsValidating(false);
        onValidationChange?.(true);
        return;
      }

      try {
        // Check if all required fields have values
        const formValues = formMethods.getValues();

        // Skip revalidation if snapshot of current section values didn't change
        // BUT always validate on first mount or when section changes
        try {
          const currentFields = getFieldsForSection({
            schema: resolvedSchema,
            sectionKey: currentSectionKey,
          });
          const keys = (currentFields || []).map((f) => f.name);
          const snapshot = JSON.stringify(
            keys.reduce((acc, k) => {
              acc[k] = formValues[k];
              return acc;
            }, {})
          );
          
          // Always validate on first check for this section, or if values changed
          const isFirstValidation = !hasValidatedOnMountRef.current;
          const valuesChanged = snapshot !== lastValidationHashRef.current;
          
          if (!isFirstValidation && !valuesChanged) {
            setIsValidating(false);
            return;
          }
          
          lastValidationHashRef.current = snapshot;
          hasValidatedOnMountRef.current = true;
        } catch (e) {
          // ignore snapshot issues
        }

        // Consider required=true, but also treat selected fields as valid if already present
        const requiredFields = currentFields
          .filter((field) => field.required === true)
          .map((field) => field.name);

        // Evaluate boolean at-least-one rule for sections comprised of boolean choices
        const booleanFieldDefs = currentFields.filter(
          (f) =>
            f.type === "single_boolean_choice" ||
            f.type === "composed_boolean_choice" ||
            f.type === "multi_boolean_choice"
        );
        // Section-level required override: if the schema marks section.required === false, auto-valid
        const currentSectionDef = Array.isArray(resolvedSchema)
          ? resolvedSchema.find((s) => s && s.section === currentSectionKey)
          : null;
        if (currentSectionDef && currentSectionDef.required === false) {
          setIsCurrentStepValid(true);
          setIsValidating(false);
          onValidationChange?.(true);
          return;
        }
        const booleanTrueCount = booleanFieldDefs.reduce((acc, f) => {
          const v = formValues[f.name];
          if (f.type === "single_boolean_choice") {
            return acc + (v && v.value === true ? 1 : 0);
          }
          if (f.type === "composed_boolean_choice") {
            return acc + (v && v.status === true ? 1 : 0);
          }
          if (f.type === "multi_boolean_choice") {
            return acc + (v && v.value === true ? 1 : 0);
          }
          return acc;
        }, 0);
        const sectionRequiresAtLeastOneTrue =
          currentSectionDef?.validation?.atLeastOneTrue === true;
        if (
          booleanFieldDefs.length > 0 &&
          requiredFields.length === 0 &&
          sectionRequiresAtLeastOneTrue
        ) {
          const valid = booleanTrueCount > 0;
          setIsCurrentStepValid(valid);
          setIsValidating(false);
          onValidationChange?.(valid);
          return;
        }

        // Check if all required fields have values
        const hasRequiredFields = requiredFields.every((fieldName) => {
          const value = formValues[fieldName];
          let hasValue = false;

          // Find the field definition to check its type
          const fieldDef = currentFields.find(
            (field) => field.name === fieldName
          );

          // Special handling for checkbox fields (single_boolean_choice)
          if (fieldDef && fieldDef.type === "single_boolean_choice") {
            hasValue = value && value.value === true;
          } else if (fieldDef && fieldDef.type === "multi_select") {
            // Special handling for incomeStates - it's an object keyed by year
            if (fieldName === "incomeStates") {
              hasValue = value && typeof value === "object" && !Array.isArray(value) &&
                Object.values(value).some((states) => Array.isArray(states) && states.length > 0);
            } else {
              // Default multi_select - array
              hasValue = Array.isArray(value) && value.length > 0;
            }
          } else if (fieldDef && fieldDef.type === "single_select") {
            hasValue =
              value !== undefined && value !== null && String(value).trim() !== "";
          } else if (fieldDef && fieldDef.type === "multi_address") {
            hasValue = Array.isArray(value) && value.length > 0;
          } else if (fieldDef && fieldDef.type === "single_address") {
            // Consider section valid only when required subfields have values
            const a = formValues.address;
            const c = formValues.city;
            const s = formValues.stateAddress;
            const z = formValues.zipCode;
            hasValue =
              !!(a && String(a) !== "") &&
              !!(c && String(c) !== "") &&
              !!(s && String(s) !== "") &&
              !!(z && String(z) !== "");
          } else if (fieldDef && fieldDef.type === "composed_boolean_choice") {
            hasValue = value && typeof value.status === "boolean"; // toggle interacted
          } else {
            hasValue = value !== undefined && value !== null && value !== "";
          }

          const result = hasValue;
          return result;
        });

        if (!hasRequiredFields && requiredFields.length > 0) {
          setIsCurrentStepValid(false);
          setIsValidating(false);
          onValidationChange?.(false);
          return;
        }

        // If no required fields and no boolean at-least-one rule, mark as valid
        if (requiredFields.length === 0 && !sectionRequiresAtLeastOneTrue) {
          setIsCurrentStepValid(true);
          setIsValidating(false);
          onValidationChange?.(true);
          return;
        }

        // Only validate if all required fields have values
        if (requiredFields.length > 0) {
          // Validate ALL required fields at once
          const validationResults = await Promise.all(
            requiredFields.map((fieldName) => formMethods.trigger(fieldName))
          );

          // All fields must be valid
          const isValid = validationResults.every((result) => result === true);

          // Final validation: check only the current step fields for cross-field validations
          if (isValid) {
            try {
              // Get only required field names from current step (excluding section_heading fields)
              const requiredFieldNames = currentFields
                .filter(
                  (field) =>
                    field.type !== "section_heading" && field.required === true
                )
                .map((field) => field.name);

              // Check for multi_address fields and validate address coverage
              const multiAddressFields = currentFields.filter(
                (field) => field.type === "multi_address" && field.required === true
              );
              
              // Get form values for address validation
              const formValuesForValidation = formMethods.getValues();
              
              let addressValidationPassed = true;
              if (multiAddressFields.length > 0) {
                const { validateAddressCoverage } = require("../../utils/validation/addressCoverageValidation");
                const taxYearsValue = formMethods.getValues("taxYears");
                let currentTaxYears = null;
                if (Array.isArray(taxYearsValue) && taxYearsValue.length > 0) {
                  currentTaxYears = taxYearsValue.map(year => typeof year === 'string' ? parseInt(year, 10) : year);
                }
                
                if (currentTaxYears && Array.isArray(currentTaxYears) && currentTaxYears.length > 0) {
                  for (const addressField of multiAddressFields) {
                    const addressesArray = Array.isArray(formValuesForValidation[addressField.name]) ? formValuesForValidation[addressField.name] : [];
                    const validation = validateAddressCoverage(addressesArray, currentTaxYears);
                    
                    if (!validation.isValid) {
                      addressValidationPassed = false;
                      // Store validation error for alert display (only if showErrors is true or hasAttemptedNext)
                      if (showErrors || hasAttemptedNext) {
                        setAddressValidationError({
                          missingYears: validation.missingYears,
                          errors: validation.errors,
                          missingRanges: validation.missingRanges || [],
                        });
                      }
                      break; // Stop checking other address fields if one fails
                    }
                  }
                } else {
                  // No taxYears - clear any existing address validation error
                  setAddressValidationError(null);
                }
              } else {
                // No multi_address fields - clear any existing address validation error
                setAddressValidationError(null);
              }
              
              // Clear alert if validation passed
              if (addressValidationPassed && addressValidationError) {
                setAddressValidationError(null);
              }

              let finalValidation;
              if (effectiveSubStep === 0) {
                // First validate only the required fields
                const stepValidation =
                  requiredFieldNames.length > 0
                    ? await formMethods.trigger(requiredFieldNames)
                    : true;

                // Then manually check password match
                const formValues = formMethods.getValues();
                const passwordMatch =
                  formValues.password === formValues.confirm_password;

                finalValidation = stepValidation && passwordMatch && addressValidationPassed;
              } else {
                finalValidation =
                  (requiredFieldNames.length > 0
                    ? await formMethods.trigger(requiredFieldNames)
                    : true) && addressValidationPassed;
              }
              setIsCurrentStepValid(finalValidation);
              setIsValidating(false);
              onValidationChange?.(finalValidation);
              
              // Clear address validation alert if final validation passed (Next button enabled)
              if (finalValidation && addressValidationError) {
                setAddressValidationError(null);
              }
            } catch (error) {
              // final validation error hidden in production
              setIsCurrentStepValid(false);
              setIsValidating(false);
              onValidationChange?.(false);
            }
          } else {
            setIsCurrentStepValid(isValid);
            setIsValidating(false);
            onValidationChange?.(isValid);
          }
        } else {
          // No required fields, consider valid
          setIsCurrentStepValid(true);
          setIsValidating(false);
          onValidationChange?.(true);
        }
      } catch (error) {
        setIsCurrentStepValid(false);
        setIsValidating(false);
        onValidationChange?.(false);
      }
    };

    // Store validateCurrentStep in ref
    validateCurrentStepRef.current = validateCurrentStep;
    
    // Initial validation (silently, for button state)
    // Use a delay to ensure form values are populated from saved data
    const initialValidationTimeout = setTimeout(() => {
      validateCurrentStep(false);
    }, 300);

    // Track if we've done initial validation to avoid duplicate calls
    let hasRunInitialValidation = false;

    // Watch for changes in form values with debounce
    const subscription = formMethods.watch((value, { name, type }) => {
      setRenderTick((t) => t + 1);
      
      // If form was reset (type === undefined or all values changed), trigger validation
      const isFormReset = !name && !type;
      
      // On first watch call (when form is populated), trigger validation
      // This handles the case when form is loaded with saved data (refresh/navigation back)
      if (!hasRunInitialValidation) {
        hasRunInitialValidation = true;
        // Small delay to ensure all values are populated, especially phone field
        setTimeout(() => {
          validateCurrentStep(false);
        }, 200);
      }
      
      // Only validate if we have a name (specific field changed) or form was reset
      if (name || isFormReset) {
        // If form was reset, mark as initialized
        if (isFormReset) {
          formInitializedRef.current = true;
          validationRetryCountRef.current = 0;
        }
        // Get current required fields from the schema
        const currentSection = Array.isArray(resolvedSchema)
          ? resolvedSchema.find((s) => s && s.section === currentSectionKey)
          : null;

        const currentRequiredFields =
          currentSection?.fields
            ?.filter((field) => field.required === true)
            ?.map((field) => field.name) || [];

        // Check if all required fields have values
        const allRequiredFieldsHaveValues = currentRequiredFields.every(
          (fieldName) => {
            const fieldValue = value[fieldName];
            let hasValue = false;

            // Find the field definition to check its type
            const fieldDef = currentSection?.fields?.find(
              (field) => field.name === fieldName
            );

            // Special handling for checkbox fields (single_boolean_choice)
            if (fieldDef && fieldDef.type === "single_boolean_choice") {
              hasValue = fieldValue && fieldValue.value === true;
            } else {
              hasValue =
                fieldValue !== undefined &&
                fieldValue !== null &&
                fieldValue !== "";
            }

            const result = hasValue;
            return result;
          }
        );

        // Only validate if all required fields have values OR section is boolean-only/marked non-required
        const isBooleanOnlySection = Array.isArray(currentSection?.fields)
          ? currentSection.fields.every(
              (f) =>
                f?.type === "single_boolean_choice" ||
                f?.type === "composed_boolean_choice" ||
                f?.type === "section_heading"
            )
          : false;
        const isNonRequiredSection = currentSection?.required === false;
        const hasAtLeastOneTrueRule =
          currentSection?.validation?.atLeastOneTrue === true;
        if (
          allRequiredFieldsHaveValues ||
          isBooleanOnlySection ||
          isNonRequiredSection ||
          hasAtLeastOneTrueRule
        ) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          timeoutId = setTimeout(() => {
            // Only validate silently for button state, don't show errors
            validateCurrentStep(false);
          }, 500); // Increased debounce time
        } else {
          // Set invalid if not all required fields have values
          setIsCurrentStepValid(false);
          setIsValidating(false);
          onValidationChange?.(false);
        }
      }
    });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (initialValidationTimeout) {
        clearTimeout(initialValidationTimeout);
      }
      subscription.unsubscribe();
    };
  }, [
    formMethods,
    currentSectionKey,
    onValidationChange,
    resolvedSchema,
    effectiveSubStep,
    isValidating,
  ]);
  
  // Reset validation flag when section changes to force revalidation
  useEffect(() => {
    hasValidatedOnMountRef.current = false;
    lastValidationHashRef.current = "";
  }, [currentSectionKey]);
  
  // Trigger validation when initialFormState changes (form was reset with existing data)
  useEffect(() => {
    if (formMethods && initialFormState && currentSectionKey && validateCurrentStepRef.current) {
      // Wait a bit for form.reset() to complete and values to be populated
      // Use multiple attempts to ensure phone field and other fields are ready
      let attemptCount = 0;
      const maxAttempts = 5;
      let timeoutId = null;
      
      const tryValidation = () => {
        attemptCount++;
        const formValues = formMethods.getValues();
        const currentSection = Array.isArray(resolvedSchema)
          ? resolvedSchema.find((s) => s && s.section === currentSectionKey)
          : null;
        
        const requiredFields = currentSection?.fields
          ?.filter((field) => field.required === true)
          ?.map((field) => field.name) || [];
        
        // Check if all required fields have values
        const allFieldsHaveValues = requiredFields.every((fieldName) => {
          const value = formValues[fieldName];
          // Special handling for phone - check if it has 10 digits
          if (fieldName === "phone") {
            const digits = String(value || "").replace(/\D/g, "");
            return digits.length === 10;
          }
          return value !== undefined && value !== null && value !== "";
        });
        
        // If all fields have values or we've tried enough times, run validation
        if (allFieldsHaveValues || attemptCount >= maxAttempts) {
          formInitializedRef.current = true;
          validationRetryCountRef.current = 0;
          if (validateCurrentStepRef.current) {
            validateCurrentStepRef.current(false);
          }
        } else {
          // Try again after a short delay
          timeoutId = setTimeout(tryValidation, 200);
        }
      };
      
      // Start validation attempts
      timeoutId = setTimeout(tryValidation, 200);
      
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [initialFormState, formMethods, currentSectionKey, resolvedSchema]);
  
  // Use ref to track current step for event listener
  const currentStepRef = useRef(effectiveSubStep);
  currentStepRef.current = effectiveSubStep;

  // Listen for navigate to specific step event
  useEffect(() => {
    const handleNavigateToStep = (event) => {
      const { step } = event.detail;
      setInternalDirection("prev");
      setInternalSubStep(step);
    };

    const handleSetWizardError = (event) => {
      const { error } = event.detail;
      setErrorSummary(error);
    };

    document.addEventListener("navigateToStep", handleNavigateToStep);
    document.addEventListener("setWizardError", handleSetWizardError);

    return () => {
      document.removeEventListener("navigateToStep", handleNavigateToStep);
      document.removeEventListener("setWizardError", handleSetWizardError);
    };
  }, []);

  // Use refs to avoid useEffect re-runs
  const totalSubStepsRef = useRef(totalSubSteps);
  const errorSummaryRef = useRef(errorSummary);
  const onSubmitRef = useRef(onSubmit);
  const onSubStepCompleteRef = useRef(onSubStepComplete);
  const formMethodsRef = useRef(formMethods);
  const formStateRef = useRef(formState);

  // Update refs when values change
  totalSubStepsRef.current = totalSubSteps;
  errorSummaryRef.current = errorSummary;
  onSubmitRef.current = onSubmit;
  onSubStepCompleteRef.current = onSubStepComplete;
  formMethodsRef.current = formMethods;
  formStateRef.current = formState;

  // Listen for continue to next step event
  useEffect(() => {
    const handleContinueToNextStep = () => {
      // Clear any error summary
      if (errorSummaryRef.current) setErrorSummary("");

      // Use functional update to ensure we have the latest step value
      setInternalSubStep((currentStep) => {
        const isLastSubStep = currentStep >= totalSubStepsRef.current - 1;

        if (!isLastSubStep) {
          const nextStep = currentStep + 1;
          // Validate that nextStep is within bounds
          if (nextStep < totalSubStepsRef.current) {
            setInternalDirection("next");
            return nextStep;
          }
          return currentStep;
        }

        // Last sub-step: submit form data
        if (typeof onSubmitRef.current === "function") {
          if (formMethodsRef.current) {
            // Use react-hook-form data
            const formData = formMethodsRef.current.getValues();
            onSubmitRef.current(formData);
          } else {
            // Fallback to old form state
            onSubmitRef.current(formStateRef.current || {});
          }
        }
        
        return currentStep;
      });
    };

    document.addEventListener("continueToNextStep", handleContinueToNextStep);

    return () => {
      document.removeEventListener(
        "continueToNextStep",
        handleContinueToNextStep
      );
    };
  }, []);

  const currentSubStepLabel = humanize(currentSectionKey);

  // Get current section definition to access infoTooltip
  const currentSectionDef = Array.isArray(resolvedSchema)
    ? resolvedSchema.find((s) => s && s.section === currentSectionKey)
    : null;
  const infoTooltip = currentSectionDef?.infoTooltip || "";

  // Resolve fields for current section
  const sectionsDef = resolvedSchema;
  const fields = getFieldsForSection({
    schema: sectionsDef,
    sectionKey: currentSectionKey,
  });

  const handleChange = (fieldName, value) => {
    // Use react-hook-form if available
    if (formMethods) {
      formMethods.setValue(fieldName, value);
    } else if (typeof setFormState === "function") {
      setFormState((prev) => ({
        ...prev,
        [fieldName]: {
          ...(prev?.[fieldName] || {}),
          value,
        },
      }));
    }
  };

  const handleToggleComposedField = (fieldName, defaultValue) => {
    // Use react-hook-form if available
    if (formMethods) {
      const currentValue = formMethods.getValues(fieldName);
      const currentStatus = Boolean(currentValue?.status);
      const newStatus = !currentStatus;
      const newValue = newStatus
        ? Math.max(currentValue?.value || 0, 1)
        : defaultValue;
      formMethods.setValue(
        fieldName,
        {
          status: newStatus,
          value: newValue,
        },
        { shouldValidate: true, shouldDirty: true }
      );
    } else if (typeof setFormState === "function") {
      setFormState((prev) => ({
        ...prev,
        [fieldName]: {
          ...(prev?.[fieldName] || {}),
          status: !prev?.[fieldName]?.status,
          value: !prev?.[fieldName]?.status
            ? Math.max(prev?.[fieldName]?.value || 0, 1)
            : defaultValue,
        },
      }));
    }
  };

  const sectionRef = useRef(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    el.classList.remove("entering");
    setInternalAnimating(true);
    const t1 = setTimeout(() => {
      el.classList.add("entering");
    }, 50);
    const t2 = setTimeout(() => {
      setInternalAnimating(false);
    }, 850);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [effectiveSubStep, resolvedSchema]);

  // Also trigger wrapper animation when direction changes
  useEffect(() => {
    setInternalAnimating(true);
    const t = setTimeout(() => setInternalAnimating(false), 850);
    return () => clearTimeout(t);
  }, [internalDirection]);

  // Reset summary visibility when entering a new sub-step
  useEffect(() => {
    setShowSummary(false);
    hasUserInteractedRef.current = false;
    setHasAttemptedNext(false);
    setAddressValidationError(null); // Clear address validation error when changing steps
  }, [effectiveSubStep, currentSectionKey]);
  
  // Clear address validation alert when Next button becomes enabled
  useEffect(() => {
    if (isCurrentStepValid && addressValidationError) {
      setAddressValidationError(null);
    }
  }, [isCurrentStepValid, addressValidationError]);

  // Mark interaction when any element in the section blurs (captures Tab navigation too)
  // Note: We don't validate on blur - validation only happens when clicking "Next"
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const onBlurCapture = async (evt) => {
      if (!hasUserInteractedRef.current) {
        hasUserInteractedRef.current = true;
      }
      // Don't trigger validation on blur - only mark as touched for tracking
      // Validation will only happen when user clicks "Next"
      try {
        if (!formMethods || !currentSectionKey) return;
        const blurredName = evt?.target?.name;
        if (blurredName) {
          // Mark the field as touched but don't validate
          const currentVal = formMethods.getValues(blurredName);
          formMethods.setValue(blurredName, currentVal, {
            shouldTouch: true,
            shouldValidate: false, // Don't validate on blur
            shouldDirty: false,
          });
        }
      } catch (_) {}
    };
    el.addEventListener("blur", onBlurCapture, true);
    return () => el.removeEventListener("blur", onBlurCapture, true);
  }, [sectionRef, currentSectionKey, formMethods, resolvedSchema]);

  // Cache formState slices at render to subscribe updates (RHF requires reading properties during render)
  const currentErrors = formMethods?.formState?.errors;
  const currentTouched = formMethods?.formState?.touchedFields;

  useEffect(() => {
    if (!formMethods || !currentSectionKey) return;
    const errorsObj = currentErrors || {};
    const fieldsDef =
      getFieldsForSection({
        schema: resolvedSchema,
        sectionKey: currentSectionKey,
      }) || [];
    const sectionFieldNames = expandSectionFieldNames(fieldsDef);
    const sectionErrorCount = sectionFieldNames.reduce(
      (acc, n) => acc + (errorsObj[n] ? 1 : 0),
      0
    );
    if (hasUserInteractedRef.current && sectionErrorCount >= 1) {
      setShowSummary(true);
    }
  }, [
    currentTouched,
    currentErrors,
    currentSectionKey,
    resolvedSchema,
    formMethods,
  ]);

  useEffect(() => {
    if (!formMethods || !currentSectionKey) return;
    const fieldsDef =
      getFieldsForSection({
        schema: resolvedSchema,
        sectionKey: currentSectionKey,
      }) || [];
    const sectionFieldNames = expandSectionFieldNames(fieldsDef);
    const sub = formMethods.watch(() => {
      const errorsObj = currentErrors || {};
      const sectionErrorCount = sectionFieldNames.reduce(
        (acc, n) => acc + (errorsObj[n] ? 1 : 0),
        0
      );
      const shouldShow = hasUserInteractedRef.current && sectionErrorCount >= 1;
      if (shouldShow && !showSummary) {
        setShowSummary(true);
      }
    });
    return () => sub && sub.unsubscribe && sub.unsubscribe();
  }, [
    formMethods,
    currentSectionKey,
    resolvedSchema,
    showSummary,
    currentTouched,
    currentErrors,
  ]);

  const goPrev = () => {
    setInternalDirection("prev");
    // Use functional update to ensure we have the latest state value
    setInternalSubStep((currentStep) => {
      if (currentStep > 0) {
        const newStep = currentStep - 1;
        // Force revalidation after moving back to ensure Next reflects current values
        setTimeout(() => {
          try {
            if (formMethods) {
              const snapshot = formMethods.getValues();
              // Nudge RHF by setting one of the current section fields again
              const currentFields = getFieldsForSection({
                schema: resolvedSchema,
                sectionKey: metadata.subStepSections?.[newStep] || null,
              });
              const firstField = currentFields.find((f) => f && f.name)?.name;
              if (firstField) {
                formMethods.setValue(firstField, snapshot[firstField], {
                  shouldValidate: true,
                  shouldDirty: false,
                });
              }
            }
          } catch (_) {}
        }, 50);
        return newStep;
      } else {
        if (typeof onPrevFromFirstSubStep === "function") {
          onPrevFromFirstSubStep();
        }
        return currentStep;
      }
    });
  };

  const goNext = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();

    // Prevent multiple simultaneous calls to goNext
    if (isNavigatingRef.current) {
      return;
    }

    // Always validate when "Next" is clicked, regardless of button state
    // This ensures errors are shown and navigation is prevented if validation fails
    setHasAttemptedNext(true);
    
    try {
      const currentFields = getFieldsForSection({
        schema: resolvedSchema,
        sectionKey: currentSectionKey,
      });
      const sectionFieldNames = expandSectionFieldNames(currentFields || []);
      
      if (formMethods && sectionFieldNames.length > 0) {
        // Mark all fields as touched and validate them
        // For nested fields (like cryptoTransaction.fields.exchanges), we validate the parent field
        sectionFieldNames.forEach((fieldName) => {
          const currentVal = formMethods.getValues(fieldName);
          formMethods.setValue(fieldName, currentVal, {
            shouldTouch: true,
            shouldValidate: false, // Don't validate here, we'll trigger separately
            shouldDirty: false,
          });
        });
        
        // Trigger validation for all fields to show errors
        // This will validate parent fields which will validate their nested fields
        const validationResults = await Promise.all(
          sectionFieldNames.map(async (fieldName) => {
            // Get current value before triggering
            const currentValue = formMethods.getValues(fieldName);
            
            // For multi_address fields, manually validate if trigger doesn't work
            const fieldDef = currentFields.find(f => f && f.name === fieldName);
            if (fieldDef && fieldDef.type === "multi_address") {
              const { validateAddressCoverage } = require("../../utils/validation/addressCoverageValidation");
              
              // Get taxYears
              const taxYearsValue = formMethods.getValues("taxYears");
              let currentTaxYears = null;
              if (Array.isArray(taxYearsValue) && taxYearsValue.length > 0) {
                currentTaxYears = taxYearsValue.map(year => typeof year === 'string' ? parseInt(year, 10) : year);
              }
              
              // Validate if taxYears exist
              if (currentTaxYears && Array.isArray(currentTaxYears) && currentTaxYears.length > 0) {
                const addressesArray = Array.isArray(currentValue) ? currentValue : [];
                const validation = validateAddressCoverage(addressesArray, currentTaxYears);
                
                if (!validation.isValid) {
                  // Set error manually (only if validation fails)
                  // Only set error if it doesn't already exist to prevent re-render loops
                  const existingError = formMethods.formState.errors[fieldName];
                  if (!existingError || existingError.message !== validation.errors.join(" ")) {
                    formMethods.setError(fieldName, {
                      type: "manual",
                      message: validation.errors.join(" "),
                    });
                  }
                  // Store validation error for alert display
                  setAddressValidationError({
                    missingYears: validation.missingYears,
                    errors: validation.errors,
                    missingRanges: validation.missingRanges || [],
                  });
                  return false;
                } else {
                  // Validation passed - clear error and alert
                  formMethods.clearErrors(fieldName);
                  setAddressValidationError(null);
                }
              } else {
                // No taxYears - clear any existing address validation error
                setAddressValidationError(null);
              }
              
              // Clear alert if validation passed (Next button will be enabled)
              const hasAddressErrors = formMethods.formState.errors[fieldName];
              if (!hasAddressErrors && addressValidationError) {
                setAddressValidationError(null);
              }
            }
            
            // Call trigger for the field (this will use the registered validator if it exists)
            const result = await formMethods.trigger(fieldName);
            return result;
          })
        );
        
        // Check if all validations passed
        const allValid = validationResults.every((result) => result === true);
        
        // Update error summary display
        // Check for errors on parent fields and nested paths
        const errObj = formMethods?.formState?.errors || {};
        let sectionErrorCount = 0;
        sectionFieldNames.forEach((fieldName) => {
          // Check parent field errors
          if (errObj[fieldName]) {
            sectionErrorCount++;
          }
          // Check nested field errors (e.g., cryptoTransaction.fields.exchanges)
          // Find the field definition to check for nested fields
          const fieldDef = currentFields.find((f) => f?.name === fieldName);
          if (fieldDef && fieldDef.type === "multi_boolean_choice" && fieldDef.fields) {
            fieldDef.fields.forEach((nestedField) => {
              const nestedPath = `${fieldName}.fields.${nestedField.name}`;
              if (errObj[fieldName]?.fields?.[nestedField.name] || errObj[nestedPath]) {
                sectionErrorCount++;
              }
            });
          }
        });
        setShowSummary(sectionErrorCount >= 1);
        
        // If validation failed, prevent navigation and update button state
        if (!allValid) {
          setIsCurrentStepValid(false);
          setIsValidating(false);
          onValidationChange?.(false);
          isNavigatingRef.current = false;
          return; // Don't proceed - validation failed
        }
        
        // Validation passed - update state to allow navigation
        setIsCurrentStepValid(true);
        setIsValidating(false);
        onValidationChange?.(true);
      }
    } catch (_) {
      setShowSummary(false);
      setIsCurrentStepValid(false);
      setIsValidating(false);
      onValidationChange?.(false);
      isNavigatingRef.current = false;
      return;
    }
    
    // If we get here, validation passed - proceed with navigation

    // Call onBeforeNext callback if provided
    if (onBeforeNext && typeof onBeforeNext === "function") {
      const shouldContinue = await onBeforeNext(effectiveSubStep, e);
      if (shouldContinue === false) {
        // Reset navigating flag since we're not proceeding
        isNavigatingRef.current = false;
        return; // Don't proceed, let the parent component handle it
      }
    }

    // Clear any error summary
    if (errorSummary) setErrorSummary("");

    // Set navigating flag to prevent multiple calls
    // Set it here, after all validation checks pass
    isNavigatingRef.current = true;

    // Get current step value (don't use state setter for this)
    const currentStep = effectiveSubStep;
    const isLastSubStep = currentStep >= totalSubSteps - 1;
    
    // Call onSubStepComplete BEFORE advancing to check API response
    let apiSucceeded = true; // Default to true if no onSubStepComplete handler
    if (typeof onSubStepCompleteRef.current === "function") {
      const currentFormData = formMethodsRef.current
        ? formMethodsRef.current.getValues()
        : formStateRef.current || {};
      
      try {
        const result = await onSubStepCompleteRef.current(currentFormData, currentStep);
        // If result is explicitly false, API failed
        if (result === false) {
          apiSucceeded = false;
        } else if (result === true || result === undefined) {
          // If result is true or undefined, consider it success
          apiSucceeded = true;
        }
      } catch (error) {
        // If promise rejects, API failed
        console.error("Error in onSubStepComplete:", error);
        apiSucceeded = false;
      }
    }
    
    // Only proceed if API call succeeded (status 200)
    if (!apiSucceeded) {
      isNavigatingRef.current = false;
      return; // Stay on current step, error already shown by onSubStepComplete
    }
    
    // API succeeded - proceed with navigation
    if (!isLastSubStep) {
      // Move to next step
      setInternalDirection("next");
      setInternalSubStep((prev) => {
        const next = prev + 1;
        if (next < totalSubSteps) {
          return next;
        }
        return prev;
      });
      
      // Reset navigating flag after state update completes
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 50);
    } else {
      // Last step: call onSubmit
      if (typeof onSubmitRef.current === "function") {
        const formData = formMethodsRef.current
          ? formMethodsRef.current.getValues()
          : formStateRef.current || {};
        onSubmitRef.current(formData);
      }
      // Reset navigating flag after submit
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 50);
    }
  };

  // Renderer: prefer provided renderField, else dynamic renderer adapter
  const effectiveRenderField = (field) =>
    dynamicRenderField({
      field,
      formState: formMethods ? formMethods.getValues() : formState || {},
      setFormState: setFormState || (() => {}),
      hasAttemptedNext: hasAttemptedNext, // Pass flag to control error display
      handleToggleComposedField: handleToggleComposedField,
      handleValueChange: handleChange,
      formMethods, // Pass react-hook-form methods
    });

  const Progress = (
    <>
      <div className="progress-bar-container">
        <div
          className="progress-bar-fill"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
      <div className="sub-step-dots">
        {Array.from({ length: totalSubSteps }, (_, index) => (
          <div
            key={index}
            className={`sub-step-dot ${
              index <= effectiveSubStep ? "active" : ""
            }`}
          ></div>
        ))}
      </div>
    </>
  );

  const FieldsSection = (
    <section ref={sectionRef} className="form-section wizard-section">
      <FormSectionWrapper 
        title={currentSubStepLabel} 
        infoTooltip={infoTooltip}
        showFieldSeparators={currentSectionKey !== "Addresses"}
      >
        {Progress}
        {/* Address validation alert */}
        {addressValidationError && addressValidationError.missingYears && addressValidationError.missingYears.length > 0 && (
          <div 
            className="address-validation-alert" 
            style={{
              backgroundColor: '#FFF3CD',
              border: '1px solid #FFC107',
              borderRadius: '4px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#004789',
              fontSize: '14px'
            }}
          >
            <style>{`
              .address-validation-alert,
              .address-validation-alert * {
                color: #004789 !important;
              }
            `}</style>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              Address Coverage Required
            </div>
            <div>
              {addressValidationError.missingRanges && addressValidationError.missingRanges.length > 0 && (
                <div>
                  <ul style={{ marginLeft: '20px', marginTop: '4px', paddingLeft: '0' }}>
                    {addressValidationError.missingRanges.map((yearData, yearIndex) => (
                      <li key={yearIndex} style={{ marginBottom: '4px' }}>
                        <strong>{yearData.year}:</strong> {yearData.monthList}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
        {(() => {
          // Group fields that should be displayed side by side
          const fieldGroups = [
            ["firstname", "lastname"],
            ["email", "phone"],
            ["dBirth", "occupation"]
          ];
          
          // Create a map of field name to group for quick lookup
          const fieldToGroupMap = new Map();
          fieldGroups.forEach((group, groupIndex) => {
            group.forEach((fieldName) => {
              fieldToGroupMap.set(fieldName, groupIndex);
            });
          });
          
          // Track which fields have been processed
          const processed = new Set();
          const processedFields = [];
          
          fields.forEach((field, index) => {
            // Skip if already processed
            if (processed.has(index)) return;
            
            const groupIndex = fieldToGroupMap.get(field.name);
            
            if (groupIndex !== undefined) {
              // This field is part of a group - find all fields in the group
              const group = fieldGroups[groupIndex];
              const groupFields = [];
              const groupIndices = [];
              
              // Find all fields in this group that haven't been processed
              fields.forEach((f, idx) => {
                if (group.includes(f.name) && !processed.has(idx)) {
                  groupFields.push(f);
                  groupIndices.push(idx);
                  processed.add(idx);
                }
              });
              
              // Render the group
              processedFields.push(
                <div key={`group-${index}`} className="form-field-group">
                  {groupFields.map((f) => (
                    <div key={f.name} className="sub-step-field form-field-group-item">
                      {effectiveRenderField(f)}
                    </div>
                  ))}
                </div>
              );
            } else {
              // Render field normally
              processedFields.push(
                <div key={field.name} className="sub-step-field">
                  {effectiveRenderField(field)}
                </div>
              );
              processed.add(index);
            }
          });
          
          return processedFields;
        })()}
      </FormSectionWrapper>
    </section>
  );

  const Inner = (
    <div className="sub-step-progress-container">
      {FieldsSection}
      <div className="prevNext">
        <button
          type="button"
          className="cta-button secondary"
          onClick={goPrev}
        >
          <b>Back</b>
        </button>
        <button
          type="button"
          className="cta-button primary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Check if already navigating to prevent double-triggering
        if (isNavigatingRef.current) {
          return;
        }
            
            goNext(e);
          }}
          disabled={!formMethods || !isCurrentStepValid || isValidating || isNavigatingRef.current}
        >
          <b>
            {effectiveSubStep >= totalSubSteps - 1 ? nextButtonText : "Next"}
          </b>
        </button>
      </div>
    </div>
  );

  const Content = (
    <div
      className={`sub-step-form-container ${effectiveDirection} ${
        effectiveAnimating ? "animating" : ""
      }`}
    >
      {Inner}
    </div>
  );

  return (
    <Form
      method="put"
      onSubmit={(e) => {
        e.preventDefault();
        // Only handle form submission if button wasn't already clicked
        // (button click will handle navigation, form submit is just a fallback)
        if (!isNavigatingRef.current) {
          goNext(e);
        }
      }}
    >
      <input type="hidden" name="wizard_id" value={wizardInstanceId} />
      <input type="hidden" name="wizard_type" value="vertical" />
      {Content}
    </Form>
  );
}

export default VerticalFormWizard;
